import { Injectable } from '@angular/core';

import { Logger } from '@frontend/vanilla/core';

import { Categories, Subcategory } from '../casino-lobby-manager/lobby-details-model/categories.model';
import { CategoryFeatureMetadata } from '../casino-lobby-manager/lobby-details-model/category-feature-metadata-model';
import { GameMetaDataMap } from '../casino-lobby-manager/lobby-details-model/game-metadata.model';
import { LobbyItemBaseModel } from '../casino-lobby-manager/lobby-details-model/lobby-item.model';
import { FIXEDPREFIX_TO_KEY, GameMetadataTable, LobbyTable } from './casino-index-db.model';
import { CasinoIndexDBService } from './casino-index-db.service';

@Injectable({
    providedIn: 'root',
})
export class LmtRecordToModelReaderService {
    constructor(
        private casinoIndexDBService: CasinoIndexDBService,
        private logger: Logger,
    ) {}

    async readLobbyDataFromIndexDb(
        lobbyType: string,
        expirationInterval: any,
        cacheKey: string,
        collectionKey: string,
    ): Promise<LobbyItemBaseModel | null> {
        const dataIdentifierKey = cacheKey.replace(FIXEDPREFIX_TO_KEY, '');
        const lobbyDetails = await this.getLobby(lobbyType, dataIdentifierKey);
        if (lobbyDetails == null) {
            return null;
        }
        const isLobbyDataExpired = await this.casinoIndexDBService.checkCasinoIndexDBExpired(lobbyType, dataIdentifierKey, expirationInterval);
        if (isLobbyDataExpired) {
            await this.casinoIndexDBService.clearAllTablesForCollectionKey(collectionKey);
            this.logger.info('lobby details Expired for', lobbyType);
            return null;
        }

        const [categories, gamesMetaData, stickerDetails, catVsFeatureMetadata, categoryNameLangMap] = await Promise.all([
            this.getCategories(lobbyType, dataIdentifierKey),
            this.getGameMetaData(lobbyType, dataIdentifierKey),
            this.getStickerDetails(lobbyType, dataIdentifierKey),
            this.getCatVsFeatureMetadata(lobbyType, dataIdentifierKey),
            this.getCategoryNameLangMap(lobbyType, dataIdentifierKey),
        ]);
        const lobbyItem: LobbyItemBaseModel = new LobbyItemBaseModel();
        lobbyItem.lsCategoriesMap = categories ? categories : [];
        lobbyItem.lsGameMetaDataMap = gamesMetaData ? gamesMetaData : [];
        lobbyItem.lsStickerNameMap = stickerDetails ? stickerDetails : [];
        lobbyItem.lbyTempName = lobbyDetails.templateName;
        lobbyItem.optSegName = lobbyDetails.optSegName;
        lobbyItem.lbyTargetType = lobbyDetails.lobbyTargetType;
        lobbyItem.lobbyGameIconImageSuffix = lobbyDetails.lobbyGameIconImageSuffix;
        lobbyItem.catVsFeatureMetadata = catVsFeatureMetadata ? catVsFeatureMetadata : [];
        lobbyItem.lsCategorytNameLangMap = categoryNameLangMap ? categoryNameLangMap : [];
        this.logger.info(`[readLobbyDataFromIndexDb] LobbyItemBaseModel successfully created: {lobbyType}`, dataIdentifierKey, lobbyItem);

        return lobbyItem;
    }

    async getCategories(lobbyType: string, dataIdentifierKey: string) {
        const [categories, subCategories = []] = await Promise.all([
            this.casinoIndexDBService.getCategories(lobbyType, dataIdentifierKey),
            this.casinoIndexDBService.getTransformedSubCategories(lobbyType, dataIdentifierKey),
        ]);
        if (!categories) {
            return null;
        }

        const transformedCategories: [string, Categories][] = categories?.map((category) => {
            return [
                String(category.categoryId),
                {
                    categoryid: String(category.categoryId),
                    route: category.route,
                    categoryname: category.categoryName,
                    lmtCategoryId: category.lmtCategoryId,
                    engCategoryName: category.engCategoryName,
                    isSubCatVisible: category.subCategoryVisible,
                    isNavigationCategory: category.navigationCategory,
                    gamelist: category.games,
                    subcategories: subCategories.filter((sub) => sub.parentCategoryId === category.categoryId),
                    lmtSubCategories: category.lmtSubCategories,
                    sitecoreData: category.sitecoreData,
                    sid: category.sid,
                    rowcount: category.rowcount,
                    iconpositions: category.iconpositions,
                },
            ];
        });

        const transformedSubCategories: [string, Subcategory][] = subCategories.map((subcategory) => [String(subcategory.categoryid), subcategory]);
        let combinedCategoriesAndSubcategories: [string, Categories | Subcategory][] = [...transformedCategories, ...transformedSubCategories];
        return combinedCategoriesAndSubcategories;
    }

    async getGameMetaData(lobbyType: string, dataIdentifierKey: string): Promise<[string, GameMetaDataMap][]> {
        const allGamesGameMetaData = await this.casinoIndexDBService.getGameMetadata(lobbyType, dataIdentifierKey);
        if (!allGamesGameMetaData || !allGamesGameMetaData.length) {
            return [];
        }
        const transformedAllGamesGameMetadata: [string, any][] = allGamesGameMetaData.map((gameMetaData: GameMetadataTable) => {
            const transformedGameMetadata: any = {
                game: gameMetaData.game,
                name: gameMetaData.name,
                provider: gameMetaData.provider,
                sid: gameMetaData.sid,
                ownerId: gameMetaData.ownerId,
                metaLobbyType: gameMetaData.metaLobbyType,
            };
            return [transformedGameMetadata.game, transformedGameMetadata];
        });
        return transformedAllGamesGameMetadata;
    }

    async getCatVsFeatureMetadata(lobbyType: string, dataIdentifierKey: string): Promise<CategoryFeatureMetadata[] | []> {
        const categoriesFeatureMetadata: CategoryFeatureMetadata[] | [] = await this.casinoIndexDBService.getCatVsFeatureMetadata(
            lobbyType,
            dataIdentifierKey,
        );
        if (!categoriesFeatureMetadata || !categoriesFeatureMetadata.length) {
            return [];
        }
        const transformedLsCatVsFeatureMetadata = categoriesFeatureMetadata.map(({ categoryId, subCategoryId, featureIds }) => ({
            categoryId,
            subCategoryId,
            featureIds,
        }));
        return transformedLsCatVsFeatureMetadata;
    }

    async getStickerDetails(lobbyType: string, dataIdentifierKey: string): Promise<[string, string][]> {
        const stickerDetails = await this.casinoIndexDBService.getStickerDetails(lobbyType, dataIdentifierKey);
        if (!stickerDetails || !stickerDetails.length) {
            return [];
        }
        const transformStickers: [string, string][] = stickerDetails.map((sticker) => [sticker.name, sticker.value] as [string, string]);
        return transformStickers;
    }

    async getCategoryNameLangMap(lobbyType: string, dataIdentifierKey: string): Promise<[string, string][]> {
        const categoryNameLangMap = await this.casinoIndexDBService.getCategoryNameLangMap(lobbyType, dataIdentifierKey);
        if (!categoryNameLangMap || !categoryNameLangMap.length) {
            return [];
        }
        const transformCategoryNameLangMap: [string, string][] = categoryNameLangMap.map((record) => [record.name, record.value] as [string, string]);
        return transformCategoryNameLangMap;
    }

    async getLobby(lobbyType: string, dataIdentifierKey: string): Promise<LobbyTable | null> {
        const lobbyDetails = await this.casinoIndexDBService.getLobby(lobbyType, dataIdentifierKey);
        return lobbyDetails ? lobbyDetails : null;
    }
}
